import { Add, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { ReactNode, useMemo, useState } from 'react';

import {
  CustomerIntakeField,
  CustomerIntakeFormValue,
  CustomerIntakeServiceLevel,
  CustomerIntakeServiceOptionNode,
  CustomerIntakeServiceSelection,
  decodeCustomerIntakeServiceSelection,
  encodeCustomerIntakeServiceSelection,
  getCustomerIntakeServiceSelectionLabel,
} from '../../types/CustomerIntake.type';

type CustomerIntakeServiceFieldProps = {
  field: CustomerIntakeField;
  value: CustomerIntakeFormValue | undefined;
  label: ReactNode;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  onChange: (value: CustomerIntakeFormValue) => void;
};

type FlatServiceOption = {
  key: string;
  label: string;
  selection: CustomerIntakeServiceSelection;
};

/** The identifier for a stored selection at a given level. */
const selectionKeyForLevel = (
  selection: CustomerIntakeServiceSelection,
  level: CustomerIntakeServiceLevel,
): string | undefined => {
  if (level === 'serviceType') return selection.serviceTypeId;
  if (level === 'subtype') return selection.subtypeId;
  return selection.packageId;
};

/** Flatten the snapshot into pickable options for a single level. */
const buildLevelOptions = (
  options: CustomerIntakeServiceOptionNode[],
  level: CustomerIntakeServiceLevel,
): FlatServiceOption[] => {
  if (level === 'serviceType') {
    return options.map((serviceType) => ({
      key: serviceType.id,
      label: serviceType.name,
      selection: {
        serviceTypeId: serviceType.id,
        serviceTypeName: serviceType.name,
      },
    }));
  }

  if (level === 'subtype') {
    return options.flatMap((serviceType) =>
      (serviceType.subtypes || []).map((subtype) => ({
        key: subtype.id,
        label: `${serviceType.name} › ${subtype.name}`,
        selection: {
          serviceTypeId: serviceType.id,
          serviceTypeName: serviceType.name,
          subtypeId: subtype.id,
          subtypeName: subtype.name,
        },
      })),
    );
  }

  return options.flatMap((serviceType) =>
    (serviceType.subtypes || []).flatMap((subtype) =>
      (subtype.packages || []).map((pkg) => ({
        key: pkg.id,
        label: `${serviceType.name} › ${subtype.name} › ${pkg.name}`,
        selection: {
          serviceTypeId: serviceType.id,
          serviceTypeName: serviceType.name,
          subtypeId: subtype.id,
          subtypeName: subtype.name,
          packageId: pkg.id,
          packageName: pkg.name,
        },
      })),
    ),
  );
};

const asArray = (value: CustomerIntakeFormValue | undefined): string[] => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value) return [value];
  return [];
};

const CustomerIntakeServiceField = ({
  field,
  value,
  label,
  error,
  helperText,
  disabled = false,
  onChange,
}: CustomerIntakeServiceFieldProps) => {
  const service = field.service;
  const options = useMemo(() => service?.options || [], [service?.options]);
  const multiple = Boolean(service?.multiple);
  const singleLevel: CustomerIntakeServiceLevel = service?.level || 'serviceType';

  // Builder state for the "full" cascade (Type → Sub-type → Service).
  const decodedSingle = useMemo(
    () =>
      typeof value === 'string' && value
        ? decodeCustomerIntakeServiceSelection(value)
        : null,
    [value],
  );
  const [builder, setBuilder] = useState<CustomerIntakeServiceSelection>({});

  if (!service) return null;

  const noOptions = options.length === 0;
  const errorText = error || undefined;
  const helper = errorText || helperText;

  // ---- Single-level mode ------------------------------------------------
  if (service.mode === 'single') {
    const levelOptions = buildLevelOptions(options, singleLevel);

    if (multiple) {
      const selectedKeys = new Set(
        asArray(value)
          .map((entry) => decodeCustomerIntakeServiceSelection(entry))
          .map((selection) =>
            selection ? selectionKeyForLevel(selection, singleLevel) : undefined,
          )
          .filter((key): key is string => Boolean(key)),
      );

      const toggle = (option: FlatServiceOption) => {
        const entries = asArray(value);
        const next = selectedKeys.has(option.key)
          ? entries.filter((entry) => {
              const selection = decodeCustomerIntakeServiceSelection(entry);
              return (
                !selection ||
                selectionKeyForLevel(selection, singleLevel) !== option.key
              );
            })
          : [
              ...entries,
              encodeCustomerIntakeServiceSelection(option.selection),
            ];
        onChange(next);
      };

      return (
        <FormControl component="fieldset" error={Boolean(errorText)} fullWidth>
          <Box sx={{ mb: 0.5 }}>{label}</Box>
          <FormGroup>
            {levelOptions.map((option) => (
              <FormControlLabel
                key={option.key}
                control={
                  <Checkbox
                    checked={selectedKeys.has(option.key)}
                    disabled={disabled}
                    onChange={() => toggle(option)}
                  />
                }
                label={option.label}
              />
            ))}
          </FormGroup>
          {noOptions && (
            <FormHelperText>No services are available yet.</FormHelperText>
          )}
          {helper && <FormHelperText>{helper}</FormHelperText>}
        </FormControl>
      );
    }

    const selectedKey = decodedSingle
      ? selectionKeyForLevel(decodedSingle, singleLevel) || ''
      : '';

    return (
      <FormControl fullWidth error={Boolean(errorText)} disabled={disabled}>
        <InputLabel id={`service-${field.id}-label`}>{label}</InputLabel>
        <Select
          labelId={`service-${field.id}-label`}
          id={`service-${field.id}`}
          value={selectedKey}
          label={label}
          onChange={(event) => {
            const option = levelOptions.find(
              (item) => item.key === event.target.value,
            );
            onChange(
              option
                ? encodeCustomerIntakeServiceSelection(option.selection)
                : '',
            );
          }}
        >
          <MenuItem value="">
            <em>Select an option</em>
          </MenuItem>
          {levelOptions.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {helper && <FormHelperText>{helper}</FormHelperText>}
      </FormControl>
    );
  }

  // ---- Full cascade mode ------------------------------------------------
  const activeSelection: CustomerIntakeServiceSelection = multiple
    ? builder
    : decodedSingle || {};

  const selectedType = options.find(
    (serviceType) => serviceType.id === activeSelection.serviceTypeId,
  );
  const subtypes = selectedType?.subtypes || [];
  const selectedSubtype = subtypes.find(
    (subtype) => subtype.id === activeSelection.subtypeId,
  );
  const packages = selectedSubtype?.packages || [];

  const emitSingle = (selection: CustomerIntakeServiceSelection) => {
    onChange(
      selection.serviceTypeId
        ? encodeCustomerIntakeServiceSelection(selection)
        : '',
    );
  };

  const applySelection = (selection: CustomerIntakeServiceSelection) => {
    if (multiple) setBuilder(selection);
    else emitSingle(selection);
  };

  const onTypeChange = (serviceTypeId: string) => {
    const node = options.find((item) => item.id === serviceTypeId);
    applySelection(
      node
        ? { serviceTypeId: node.id, serviceTypeName: node.name }
        : {},
    );
  };

  const onSubtypeChange = (subtypeId: string) => {
    const node = subtypes.find((item) => item.id === subtypeId);
    applySelection({
      serviceTypeId: activeSelection.serviceTypeId,
      serviceTypeName: activeSelection.serviceTypeName,
      ...(node ? { subtypeId: node.id, subtypeName: node.name } : {}),
    });
  };

  const onPackageChange = (packageId: string) => {
    const node = packages.find((item) => item.id === packageId);
    applySelection({
      serviceTypeId: activeSelection.serviceTypeId,
      serviceTypeName: activeSelection.serviceTypeName,
      subtypeId: activeSelection.subtypeId,
      subtypeName: activeSelection.subtypeName,
      ...(node ? { packageId: node.id, packageName: node.name } : {}),
    });
  };

  const addBuilderSelection = () => {
    if (!builder.serviceTypeId) return;
    const encoded = encodeCustomerIntakeServiceSelection(builder);
    const entries = asArray(value);
    if (!entries.includes(encoded)) onChange([...entries, encoded]);
    setBuilder({});
  };

  const removeSelection = (encoded: string) => {
    onChange(asArray(value).filter((entry) => entry !== encoded));
  };

  const cascade = (
    <Stack spacing={1.5}>
      <FormControl fullWidth error={Boolean(errorText)} disabled={disabled}>
        <InputLabel id={`service-${field.id}-type`}>Service type</InputLabel>
        <Select
          labelId={`service-${field.id}-type`}
          value={activeSelection.serviceTypeId || ''}
          label="Service type"
          onChange={(event) => onTypeChange(event.target.value)}
        >
          <MenuItem value="">
            <em>Select a service type</em>
          </MenuItem>
          {options.map((serviceType) => (
            <MenuItem key={serviceType.id} value={serviceType.id}>
              {serviceType.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        fullWidth
        disabled={disabled || !selectedType || subtypes.length === 0}
      >
        <InputLabel id={`service-${field.id}-subtype`}>Sub-type</InputLabel>
        <Select
          labelId={`service-${field.id}-subtype`}
          value={activeSelection.subtypeId || ''}
          label="Sub-type"
          onChange={(event) => onSubtypeChange(event.target.value)}
        >
          <MenuItem value="">
            <em>Select a sub-type</em>
          </MenuItem>
          {subtypes.map((subtype) => (
            <MenuItem key={subtype.id} value={subtype.id}>
              {subtype.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        fullWidth
        disabled={disabled || !selectedSubtype || packages.length === 0}
      >
        <InputLabel id={`service-${field.id}-package`}>Service</InputLabel>
        <Select
          labelId={`service-${field.id}-package`}
          value={activeSelection.packageId || ''}
          label="Service"
          onChange={(event) => onPackageChange(event.target.value)}
        >
          <MenuItem value="">
            <em>Select a service</em>
          </MenuItem>
          {packages.map((pkg) => (
            <MenuItem key={pkg.id} value={pkg.id}>
              {pkg.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );

  if (!multiple) {
    return (
      <FormControl component="fieldset" error={Boolean(errorText)} fullWidth>
        <Box sx={{ mb: 1 }}>{label}</Box>
        {cascade}
        {noOptions && (
          <FormHelperText>No services are available yet.</FormHelperText>
        )}
        {helper && <FormHelperText>{helper}</FormHelperText>}
      </FormControl>
    );
  }

  const addedSelections = asArray(value)
    .map((entry) => ({
      encoded: entry,
      selection: decodeCustomerIntakeServiceSelection(entry),
    }))
    .filter((item) => item.selection);

  return (
    <FormControl component="fieldset" error={Boolean(errorText)} fullWidth>
      <Box sx={{ mb: 1 }}>{label}</Box>
      {cascade}
      <Button
        type="button"
        size="small"
        variant="outlined"
        startIcon={<Add />}
        disabled={disabled || !builder.serviceTypeId}
        onClick={addBuilderSelection}
        sx={{ alignSelf: 'flex-start', mt: 1.5 }}
      >
        Add service
      </Button>
      {addedSelections.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
          {addedSelections.map((item) => (
            <Chip
              key={item.encoded}
              label={getCustomerIntakeServiceSelectionLabel(item.selection!)}
              onDelete={disabled ? undefined : () => removeSelection(item.encoded)}
              deleteIcon={<Close />}
              sx={{ mb: 1 }}
            />
          ))}
        </Stack>
      )}
      {noOptions && (
        <FormHelperText>No services are available yet.</FormHelperText>
      )}
      {helper && <FormHelperText>{helper}</FormHelperText>}
    </FormControl>
  );
};

export default CustomerIntakeServiceField;
